import { LocationStrategy, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { Component, ChangeDetectionStrategy, Output, EventEmitter, Input, inject } from '@angular/core';
import { FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { BehaviorSubject, Observable, take } from 'rxjs';
import { LoginConfigurationModel, UserResponseModel } from '@tailormap-viewer/api';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { ImageWithDescriptionComponent } from '@tailormap-viewer/shared';
import { MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle } from '@angular/material/expansion';
import { ErrorMessageComponent, AutoFocusDirective } from '@tailormap-viewer/shared';

@Component({
    selector: 'tm-login-form',
    templateUrl: './login-form.component.html',
    styleUrls: ['./login-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        MatProgressSpinner,
        ReactiveFormsModule,
        MatIcon,
        ErrorMessageComponent,
        MatButton,
        AutoFocusDirective,
        ImageWithDescriptionComponent,
        MatExpansionPanel,
        MatExpansionPanelHeader,
        MatExpansionPanelTitle,
        NgTemplateOutlet,
        AsyncPipe,
    ],
})
export class LoginFormComponent {
  private formBuilder = inject(FormBuilder);
  private locationStrategy = inject(LocationStrategy);


  @Input()
  public login$: ((username: string, password: string) => Observable<UserResponseModel>) | null = null;

  @Input({ required: true })
  public loginErrorMessage: string | undefined;

  @Input()
  public set insufficientRightsErrorMessage(insufficientRightsErrorMessage: string | null | undefined) {
    this.errorMessageSubject.next(insufficientRightsErrorMessage || '');
  }

  @Output()
  public loggedIn = new EventEmitter<UserResponseModel>();

  @Input()
  public redirectUrl: string | null | undefined;

  public loginForm = this.formBuilder.group({
    username: [ '', [Validators.required]],
    password: [ '', [Validators.required]],
  });

  private loggingInSubject = new BehaviorSubject(false);
  public loggingIn$ = this.loggingInSubject.asObservable();

  private errorMessageSubject = new BehaviorSubject('');
  public errorMessage$ = this.errorMessageSubject.asObservable();

  @Input()
  public set loginConfiguration(loginConfiguration: LoginConfigurationModel | null) {
    this._loginConfiguration = loginConfiguration;
    this.hasSSOButtons = (loginConfiguration?.ssoLinks || [])
      .filter(l => l.showForViewer)
      .length > 0;
    this.enablePasswordReset = !!loginConfiguration?.enablePasswordReset;
  }
  public get loginConfiguration(): LoginConfigurationModel | null {
    return this._loginConfiguration;
  }
  private _loginConfiguration: LoginConfigurationModel | null = null;

  public hasSSOButtons: boolean = false;
  public enablePasswordReset: boolean = true;

  public login() {
    const username = this.loginForm.get('username')?.value;
    const password = this.loginForm.get('password')?.value;
    if (!username || !password || !this.login$) {
      return;
    }
    this.loggingInSubject.next(true);
    this.login$(username, password)
      .pipe(take(1))
      .subscribe(userResponse => {
        this.loggingInSubject.next(false);
        if (userResponse.isAuthenticated) {
          this.errorMessageSubject.next('');
          this.loggedIn.emit(userResponse);
        } else {
          this.errorMessageSubject.next(this.loginErrorMessage || 'Login failed, please try again');
        }
      });
  }

  public loginSSO(ssoUrl: string) {
    if (this.redirectUrl !== undefined && this.redirectUrl !== null) {
      if (ssoUrl.indexOf('?') < 0) {
          ssoUrl += '?';
      } else {
          ssoUrl += '&';
      }

      ssoUrl += `redirectUrl=${encodeURIComponent(this.locationStrategy.prepareExternalUrl(this.redirectUrl))}`;
    }

    window.location.href = ssoUrl;
  }

  @Output() public requestPasswordReset = new EventEmitter<void>();

  public requestPasswordResetForm() {
    this.requestPasswordReset.emit();
  }

}
